(() => {
  if (customElements.get('kate-bundle-cards')) return;
  class KateBundleCards extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      this.initialize();
    }
    async initialize() {
      const editor = this.dataset.editor === 'true' || window.Shopify?.designMode === true;
      const preview = editor || this.dataset.preview === 'true' || window.Shopify?.theme?.role === 'unpublished' || window.Shopify?.theme?.role === 'development' || new URL(location.href).searchParams.has('preview_theme_id');
      this.hidden = true;
      if (this.dataset.testOnly === 'true' && !preview) return;
      this.root = this.dataset.root || '/';
      const proxy = this.dataset.proxy || '/apps/kate-bundles';
      if (!/^\/apps\/[a-z0-9-]+$/.test(proxy)) return this.unavailable('Invalid app proxy path.');
      this.endpoint = this.root.replace(/\/$/, '') + proxy;
      try {
        const response = await fetch(`${this.endpoint}?product=${encodeURIComponent(this.dataset.product)}`);
        if (!response.ok) throw new Error('Bundle rules could not be loaded.');
        this.rule = await response.json();
        if (!this.rule.products?.length) return this.unavailable(this.dataset.empty);
        this.products = (await Promise.all(this.rule.products.map(async p => {
          try {
            const res = await fetch(`${this.root}products/${encodeURIComponent(p.handle)}.js`);
            if (!res.ok) return null;
            const data = await res.json();
            const variants = data.variants.filter(v => v.available);
            if (!data.available || !variants.length) return null;
            return { ...data, handle: p.handle, gid: p.id, role: p.role, checked: p.role === 'main' || p.checked, variants };
          } catch { return null; }
        }))).filter(Boolean);
        if (!this.products.some(p => p.role === 'main') || !this.products.some(p => p.role === 'accessory')) return this.unavailable(this.dataset.empty);
        this.render();
        this.hidden = false;
      } catch { this.unavailable(this.dataset.loadError); }
    }
    unavailable(message) {
      if (this.dataset.editor !== 'true' && !window.Shopify?.designMode) return;
      this.replaceChildren(this.node('p', message || 'No available bundle. Check the rule and product availability.', 'kb-status'));
      this.hidden = false;
    }
    node(tag, text, className) {
      const el = document.createElement(tag);
      if (text) el.textContent = text;
      if (className) el.className = className;
      return el;
    }
    initialVariant(p) {
      if (p.role !== 'main') return p.variants[0].id;
      const urlVariant = new URL(location.href).searchParams.get('variant');
      const formVariant = Array.from(document.querySelectorAll('form[action*="/cart/add"] [name="id"]')).find(el => p.variants.some(v => String(v.id) === el.value))?.value;
      const preferred = [formVariant, urlVariant, this.dataset.variantId].find(id => p.variants.some(v => String(v.id) === String(id)));
      return preferred || p.variants[0].id;
    }
    render() {
      this.replaceChildren();
      if (this.dataset.title) this.append(this.node('h3', this.dataset.title));
      const grid = this.node('div', '', 'kb-cards');
      this.cards = this.products.map(p => {
        const card = this.node('article', '', 'kb-card');
        const label = this.node('label', '', 'kb-check'), check = this.node('input');
        check.type = 'checkbox'; check.checked = p.checked; check.setAttribute('aria-label', p.title);
        label.append(check);
        const media = this.node('a', '', 'kb-media'), image = this.node('img');
        media.setAttribute('aria-label', p.title);
        image.alt = p.title; image.loading = 'lazy'; image.width = 280; image.height = 280;
        media.append(image);
        const info = this.node('div', '', 'kb-info'), title = this.node('h4', '', 'kb-title');
        const titleLink = this.node('a', p.title);
        titleLink.style.color = 'inherit'; titleLink.style.textDecoration = 'none';
        title.append(titleLink);
        title.title = p.title;
        const price = this.node('p', '', 'kb-price'), select = this.node('select');
        select.setAttribute('aria-label', `${p.title} ${this.dataset.variant || 'variant'}`);
        for (const v of p.variants) {
          const option = this.node('option', v.title); option.value = String(v.id); select.append(option);
        }
        select.value = String(this.initialVariant(p));
        const item = { p, card, check, select, price, image, media, titleLink };
        check.addEventListener('change', () => this.update());
        select.addEventListener('change', () => this.update());
        info.append(title, price, select); card.append(label, media, info); grid.append(card);
        return item;
      });
      this.total = this.node('p', '', 'kb-total'); this.total.setAttribute('aria-live', 'polite');
      this.status = this.node('p', '', 'kb-status'); this.status.setAttribute('role', 'status');
      this.button = this.node('button', this.dataset.add, 'kb-add'); this.button.type = 'button';
      this.button.addEventListener('click', () => this.add());
      this.append(grid, this.total, this.button, this.status); this.update();
    }
    update() {
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || this.dataset.currency || 'USD' });
      const format = cents => formatter.format(cents / 100);
      const selected = this.cards.filter(c => c.check.checked);
      const eligible = selected.some(c => c.p.role === 'main') && selected.some(c => c.p.role === 'accessory');
      const percent = Number(this.rule.discountPercent);
      let total = 0;
      for (const item of this.cards) {
        const v = item.p.variants.find(v => String(v.id) === item.select.value);
        const productUrl = new URL(`${this.root.replace(/\/$/, '')}/products/${encodeURIComponent(item.p.handle)}`, location.href);
        if (v) productUrl.searchParams.set('variant', String(v.id));
        const previewTheme = new URL(location.href).searchParams.get('preview_theme_id');
        if (previewTheme) productUrl.searchParams.set('preview_theme_id', previewTheme);
        item.media.href = productUrl.pathname + productUrl.search;
        item.titleLink.href = item.media.href;
        const discounted = v && eligible && item.p.role === 'accessory' && item.check.checked && Number.isFinite(percent) && percent > 0 && percent <= 90;
        const value = discounted ? Math.round(v.price * (100 - percent) / 100) : v?.price;
        item.card.classList.toggle('is-selected', item.check.checked);
        item.check.disabled = !!this.submitting; item.select.disabled = !!this.submitting;
        item.price.replaceChildren();
        if (v) {
          item.price.append(this.node('span', format(value)));
          const compare = discounted ? v.price : Number(v.compare_at_price);
          if (compare > value) item.price.append(this.node('del', format(compare)));
          const source = v.featured_image?.src || v.featured_image?.url || item.p.featured_image || item.p.images?.[0];
          if (source) { item.image.src = typeof source === 'string' ? source : source.src; item.image.hidden = false; }
          else item.image.hidden = true;
          if (item.check.checked) total += value;
        }
      }
      this.total.textContent = `${this.dataset.total || 'Total'}: ${format(total)}`;
      this.button.disabled = !!this.submitting || !eligible;
      this.button.textContent = this.submitting ? this.dataset.adding : this.dataset.add;
      this.setAttribute('aria-busy', String(!!this.submitting));
      if (!this.submitting && !eligible) this.status.textContent = this.dataset.choose;
      else if (this.status.textContent === this.dataset.choose) this.status.textContent = '';
    }
    async add() {
      if (this.submitting || this.button.disabled) return;
      this.submitting = true; this.status.textContent = this.dataset.adding; this.update();
      try {
        const selected = this.cards.filter(c => c.check.checked);
        const lines = selected.map(c => ({ productId: c.p.gid, variantId: `gid://shopify/ProductVariant/${c.select.value}`, role: c.p.role, quantity: 1 }));
        const response = await fetch(this.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ruleId: this.rule.ruleId, version: this.rule.version, lines }) });
        const data = await response.json(); if (!response.ok || !data.token) throw new Error(this.dataset.error);
        const main = selected.find(c => c.p.role === 'main');
        const mainSku = main.p.variants.find(v => String(v.id) === main.select.value)?.sku || '';
        const cart = await fetch(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: selected.map(c => ({ id: Number(c.select.value), quantity: 1, properties: { _kb_intent: data.token, _kb_source: 'bundle', _kb_main: main.p.gid, _kb_main_sku: mainSku, _kb_rule: this.rule.ruleId, _kb_version: String(this.rule.version) } })) }) });
        if (!cart.ok) throw new Error(this.dataset.error);
        this.status.textContent = this.dataset.success;
        location.assign(`${this.root}cart`);
      } catch { this.status.textContent = this.dataset.error; }
      finally { this.submitting = false; this.update(); }
    }
  }
  customElements.define('kate-bundle-cards', KateBundleCards);
})();
